import type MockAdapter from "axios-mock-adapter"
import { EmployeeDto } from "@/modules/onboarding/employee/dtos"

const now = () => new Date().toISOString()

const EMPLOYEE_BY_ID = new RegExp("^/api/onboarding/employees/[^/]+$")

let employees: EmployeeDto[] = [
  {
    id: "emp_1",
    firstName: "John",
    lastName: "Doe",
    email: "john.doe@company.com",
    jobTitle: "Frontend Developer",
    status: 2,
    createdAt: now(),
  },
  {
    id: "emp_2",
    firstName: "Sarah",
    lastName: "Khan",
    email: "sarah.khan@company.com",
    jobTitle: "HR Coordinator",
    status: 3,
    createdAt: now(),
  },
]

function createId() {
  return `emp-${Math.random().toString(36).slice(2, 9)}`
}

function getSearchTerm(config: { url?: string; params?: unknown }) {
  const paramsObj =
    config.params && typeof config.params === "object"
      ? (config.params as Record<string, unknown>)
      : undefined
  const paramsSearchTerm = paramsObj?.searchTerm
  if (typeof paramsSearchTerm === "string") return paramsSearchTerm

  const url = config.url
  if (!url) return undefined

  try {
    const parsed = new URL(url, "http://mock.local")
    const q = parsed.searchParams.get("searchTerm")
    return q ?? undefined
  } catch {
    return undefined
  }
}

export function registerOnboardingMocks(mock: MockAdapter) {
  mock.onGet("/api/onboarding/employees").reply((config) => {
    const searchTerm = getSearchTerm(config)

    if (!searchTerm) {
      return [200, employees]
    }

    const q = searchTerm.trim().toLowerCase()
    const filtered = employees.filter((e) => {
      const fullName = `${e.firstName} ${e.lastName}`.toLowerCase()
      return fullName.includes(q) || e.email.toLowerCase().includes(q)
    })

    return [200, filtered]
  })

  mock.onGet(EMPLOYEE_BY_ID).reply((config) => {
    const id = config.url?.split("/").pop()
    if (!id) return [400, { message: "Invalid employee id" }]

    const existing = employees.find((e) => e.id === id)
    if (!existing) {
      return [404, { message: "Employee not found" }]
    }

    return [200, existing]
  })

  mock.onPost("/api/onboarding/employees").reply((config) => {
    const body = JSON.parse(config.data ?? "{}") as {
      firstName?: string
      lastName?: string
      email?: string
      jobTitle?: string
    }

    if (!body.firstName || !body.lastName || !body.email) {
      return [400, { message: "Missing required fields" }]
    }

    const newEmployee: EmployeeDto = {
      id: createId(),
      firstName: body.firstName,
      lastName: body.lastName,
      email: body.email,
      jobTitle: body.jobTitle ?? "",
      status: 1,
      createdAt: now(),
    }

    employees = [newEmployee, ...employees]

    return [201, newEmployee]
  })
}
